import { FC, useEffect, useMemo } from "react";
import { Outlet, useLocation, useOutletContext, useParams } from "react-router-dom";
import { Header } from "@components/header";
import { AppMenuNavigate } from "@components/app-menu";
import { AWSUser } from "@archetypes/auth";
import { useLazyQuery } from "@apollo/client";
import { GET_APP } from "@queries/index";
import { AppData } from "@archetypes/app";
import { buildMenuMap } from "@utils/app.util";
import { useActiveMenu } from "@contexts/active-menu-state.context";
import ErrorPageLayout from "@components/error-page-layout";
import Error404Image from "@assets/404.svg";
import { useLeftAppMenu } from "@hooks/use-left-app-menu";

const AppsLayout: FC = () => {
  const user = useOutletContext<AWSUser>();
  const { pathname } = useLocation();
  const { appId, formId } = useParams();

  const { onMenuClick, navigate } = useActiveMenu();

  const isAppView = useMemo(() => pathname.startsWith(`/apps/${appId}`), [appId, pathname]);
  const isFormView = useMemo(
    () => pathname.startsWith(`apps/${appId}/forms/${formId}`),
    [appId, formId, pathname],
  );

  const [getApp, { data: appData, loading: isAppDataLoading, error: appError }] = useLazyQuery<{
    getApp: AppData;
  }>(GET_APP);

  const { showLeftMenu, toggleLeftMenu, isMobile } = useLeftAppMenu(isAppView);

  useEffect(() => {
    if (isAppView) {
      getApp({
        variables: { input: { appId, isLiveMode: true } },
        fetchPolicy: "network-only",
      });
    }
  }, [appId, getApp, isAppView]);

  const { menuMap, homePageMenu } = useMemo(() => {
    const menuMap = buildMenuMap(appData?.getApp?.navigation?.menus ?? [], {});
    return {
      menuMap,
      homePageMenu: menuMap[appData?.getApp?.navigation?.homePageMenu as string],
    };
  }, [appData]);

  const onHeaderButtonClick = () => {
    if (homePageMenu && isAppView) {
      onMenuClick(homePageMenu, false, appId as string);
    } else {
      navigate("/apps");
    }
  };

  const context = useMemo(() => {
    return {
      menuMap,
      fontColor: appData?.getApp?.fontColor,
      primaryColor: appData?.getApp?.primaryColor,
      secondaryColor: appData?.getApp?.secondaryColor,
      user,
    };
  }, [appData, menuMap, user]);

  return (
    <main className="main-container">
      <div className="content-area w-100">
        {/* ✅ HEADER */}
        <div className="header-area">
          <Header
            basePath="/apps"
            user={user}
            isAppView={isAppView}
            appData={appData?.getApp}
            onHeaderButtonClick={onHeaderButtonClick}
            isLeftMenuVisible={showLeftMenu}
            toggleLeftMenu={toggleLeftMenu}
            isMobile={isMobile}
            primaryColor={appData?.getApp?.primaryColor}
          />
        </div>

        <div className="flex justify-center">
          {isAppView && appError ? (
            <ErrorPageLayout
              title={appError.cause?.message ?? "Error Fetching App Details"}
              image={<img src={Error404Image} alt="error" />}
            />
          ) : (
            <>
              {/* ✅ SIDEBAR */}
              {isAppView && (
                <AppMenuNavigate
                  menuData={appData?.getApp?.navigation?.menus ?? []}
                  appId={appId as string}
                  menuMap={menuMap}
                  secondaryColor={appData?.getApp?.secondaryColor}
                  fontColor={appData?.getApp?.fontColor}
                  primaryColor={appData?.getApp?.primaryColor}
                  showLeftMenu={showLeftMenu}
                  toggleLeftMenu={toggleLeftMenu}
                  isMobile={isMobile}
                />
              )}

              {/* ✅ MAIN CONTENT */}
              <div className={`app-content-area ${isAppView || isFormView ? "" : "w-100"}`}>
                {!isAppDataLoading && <Outlet context={context} />}
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default AppsLayout;
